// Index del sistema de tours. Re-exporta:
//   - `ONBOARDING_STEPS`: tour automático del primer login (sidebar + navbar).
//   - `TOUR_SECTIONS`: registro de ayudas on-demand per-ruta (botón "?").
//
// Cada feature exporta su array de `TourSection`. Aquí los conectamos al
// pathname correspondiente. Si quieres sumar una ruta nueva al menú de
// ayuda, crea un archivo `<feature>.ts` con su array y agrégalo en
// `TOUR_SECTIONS`.

export type { TourSection } from './types';
export { ONBOARDING_STEPS } from './onboarding';

import type { TourSection } from './types';
import { dashboardSections } from './dashboard';
import { productsListSections } from './products-list';
import { productsNewSections } from './products-new';
import { productsDetailSections } from './products-detail';
import { productsQuerySections } from './products-query';
import { pendingSections } from './pending';
import { aiSuggestionsSections } from './ai-suggestions';
import { aiModelsSections } from './ai-models';
import { aiConfigsListSections } from './ai-configs-list';
import { aiConfigsDetailSections } from './ai-configs-detail';
import { adminClientsListSections } from './admin-clients-list';
import { adminClientsDetailSections } from './admin-clients-detail';
import { brandsListSections, brandsDetailSections } from './brands';
import { categoriesSections } from './categories';
import {
  vehicleModelsListSections,
  vehicleModelsFormSections,
} from './vehicle-models';
import {
  usersListSections,
  usersNewSections,
  usersDetailSections,
} from './users';
import {
  auditLogsSections,
  auditLogsProductSections,
} from './audit-logs';
import {
  webhookDeliveriesListSections,
  webhookDeliveryDetailSections,
} from './webhook-deliveries';
import { adminRagSections } from './admin-rag';
import { ingestEventsSections } from './ingest-events';
import { webhookBackfillsSections } from './webhook-backfills';
import { productImportsSections } from './product-imports';
import { rolesListSections } from './roles';
import { photoStudioSections } from './photo-studio';
import { activationRulesSections } from './activation-rules';

// El match es por `location.pathname` exacto. Para detail pages que usan
// param dinámico (`:id`, `:sku`), el HelpButton hace match prefijado en
// `sectionsForRoute` (ver TourProvider) — el path aquí puede ser el
// genérico con un placeholder.
//
// IMPORTANTE: el HelpButton hoy hace match EXACTO (`pathname` === key).
// Si quieres que `/products/SKU-123` matchee, tu key debe ser dinámica
// o el provider debe hacer prefix-match. Por simplicidad aquí usamos
// el patrón con `:id`/`:sku` y dejamos al provider hacer el match.
export const TOUR_SECTIONS: Record<string, TourSection[]> = {
  '/': dashboardSections,
  '/products': productsListSections,
  '/products/new': productsNewSections,
  '/products/:sku': productsDetailSections,
  '/products/query': productsQuerySections,
  '/pending': pendingSections,
  '/ai-suggestions': aiSuggestionsSections,
  '/ai-models': aiModelsSections,
  '/ai-configs': aiConfigsListSections,
  '/ai-configs/new': aiConfigsDetailSections,
  '/ai-configs/:id': aiConfigsDetailSections,
  '/admin-clients': adminClientsListSections,
  '/admin-clients/new': adminClientsDetailSections,
  '/admin-clients/:id': adminClientsDetailSections,
  '/brands': brandsListSections,
  '/brands/new': brandsDetailSections,
  '/brands/:id': brandsDetailSections,
  '/categories': categoriesSections,
  '/vehicle-models': vehicleModelsListSections,
  '/vehicle-models/new': vehicleModelsFormSections,
  '/vehicle-models/:id': vehicleModelsFormSections,
  '/users': usersListSections,
  '/users/new': usersNewSections,
  '/users/:id': usersDetailSections,
  '/roles': rolesListSections,
  '/audit-logs': auditLogsSections,
  '/audit-logs/:productId': auditLogsProductSections,
  '/admin/rag/reindex': adminRagSections,
  '/admin/ingest-events': ingestEventsSections,
  '/admin/webhook-backfills': webhookBackfillsSections,
  '/admin/product-imports': productImportsSections,
  '/admin/activation-rules': activationRulesSections,
  '/webhook-deliveries': webhookDeliveriesListSections,
  '/webhook-deliveries/:id': webhookDeliveryDetailSections,
  // Sección TEMPORAL: ver `src/features/photo-studio/index.ts`.
  '/photo-studio': photoStudioSections,
};
